import React, { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Users, Package, Layers, Activity, AlertTriangle, Scale, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';

// Moved outside to avoid unstable nested component
const StatCard = ({ title, value, icon: Icon, color, subtitle, loading }) => (
  <div className="bg-industrial-s1 border border-industrial-border p-4 rounded-none text-left">
    <div className="flex flex-row items-center justify-between pb-2">
      <span className="text-xs font-semibold uppercase tracking-wider text-industrial-text-muted font-sans">{title}</span>
      <Icon className={`h-5 w-5 ${color}`} />
    </div>
    <div>
      <div className="text-3xl font-mono font-bold text-industrial-text">
        {loading ? '...' : value}
      </div>
      {subtitle && (
        <p className="text-[10px] font-mono text-industrial-text-muted mt-1 uppercase tracking-wider">{subtitle}</p>
      )}
    </div>
  </div>
);

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalParties: 0,
    totalCoilIns: 0,
    totalCoils: 0,
    totalJobs: 0,
    recentActivity: 0,
    weighmentStats: {
      pending_weighment_count: 0,
      gross_only_count: 0,
      net_confirmed_count: 0,
    },
    lifecycleStats: {
      OPEN: 0,
      ISSUED: 0,
      CLOSED: 0,
    },
    riskCoilsCount: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Use the new dashboard stats endpoint
        const dashboardRes = await api.dashboard.getStats();
        const data = dashboardRes.data;
        
        setStats({
          totalParties: data.total_parties || 0,
          totalCoilIns: data.total_coil_ins || 0,
          totalCoils: data.total_coils || 0,
          totalJobs: data.total_jobs || 0,
          recentActivity: data.this_week_new_entries ?? data.recent_activity ?? 0,
          weighmentStats: data.weighment_stats || {
            pending_weighment_count: 0,
            gross_only_count: 0,
            net_confirmed_count: 0,
          },
          lifecycleStats: data.lifecycle_stats || {
            OPEN: 0,
            ISSUED: 0,
            CLOSED: 0,
          },
          riskCoilsCount: data.risk_coils_count || 0,
        });
      } catch (error) {
        // Fallback to old method if new endpoint fails
        try {
          const [partiesRes, coilInsRes] = await Promise.all([
            api.parties.list({ is_active: true }),
            api.coilIns.list({ is_active: true })
          ]);
          
          // Calculate weighment stats from coil data
          let pendingCount = 0;
          let grossOnlyCount = 0;
          let netConfirmedCount = 0;
          let totalCoils = 0;
          
          coilInsRes.data.forEach(coilIn => {
            (coilIn.coils || []).forEach(coil => {
              totalCoils++;
              const status = coil.weight_status || 'NOT_WEIGHED';
              if (status === 'NOT_WEIGHED') pendingCount++;
              else if (status === 'GROSS_ONLY') grossOnlyCount++;
              else if (status === 'NET_CONFIRMED') netConfirmedCount++;
            });
          });
          
          setStats({
            totalParties: partiesRes.data.length,
            totalCoilIns: coilInsRes.data.length,
            totalCoils: totalCoils,
            totalJobs: 0,
            recentActivity: coilInsRes.data.filter(c => {
              const inwardDate = new Date(c.inward_datetime);
              const weekAgo = new Date();
              weekAgo.setDate(weekAgo.getDate() - 7);
              return inwardDate > weekAgo;
            }).length,
            weighmentStats: {
              pending_weighment_count: pendingCount,
              gross_only_count: grossOnlyCount,
              net_confirmed_count: netConfirmedCount,
            },
            lifecycleStats: {
              OPEN: 0,
              ISSUED: 0,
              CLOSED: 0,
            },
            riskCoilsCount: 0,
          });
        } catch (fallbackError) {
          toast.error('Failed to fetch dashboard stats');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const hasPendingWeighments = stats.weighmentStats.pending_weighment_count > 0;
  const hasRiskCoils = stats.riskCoilsCount > 0;

  return (
    <div className="space-y-6 text-left" data-testid="dashboard">
      <div>
        <h1 className="font-mono text-2xl font-bold uppercase tracking-wider text-industrial-text">Dashboard Overview</h1>
        <p className="text-xs font-mono text-industrial-text-muted mt-1 uppercase tracking-wider">
          Welcome back, <span className="font-semibold text-industrial-text">{user?.full_name}</span> // System Access Level: <span className="text-accent font-bold">{user?.role}</span>
        </p>
      </div>

      {/* Pending Weighment Alert */}
      {hasPendingWeighments && (
        <div className="bg-industrial-s1 border border-[#FF1744] p-4 rounded-none flex items-start gap-3 text-left" data-testid="pending-weighment-alert">
          <AlertTriangle className="h-5 w-5 text-[#FF1744] shrink-0 mt-0.5" />
          <div>
            <h4 className="font-mono text-xs font-bold text-industrial-text uppercase">Weighment Pending</h4>
            <p className="text-xs font-mono text-industrial-text-muted mt-1 uppercase tracking-wide">
              <strong>{stats.weighmentStats.pending_weighment_count}</strong> coil(s) are awaiting factory weighment.
              {hasRiskCoils && (
                <span className="ml-2 bg-[#FF6B00] text-black font-mono text-[9px] font-bold px-1.5 py-0.5 rounded-sm inline-block uppercase">
                  {stats.riskCoilsCount} AT RISK (Supplier Weight)
                </span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Main Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Active Parties"
          value={stats.totalParties}
          icon={Users}
          color="text-[#00E676]"
          loading={loading}
        />
        <StatCard
          title="Total Coil Entries"
          value={stats.totalCoilIns}
          icon={Package}
          color="text-[#00E676]"
          subtitle={`${stats.totalCoils} individual coils`}
          loading={loading}
        />
        <StatCard
          title="Active Jobs"
          value={stats.totalJobs}
          icon={Activity}
          color="text-[#FF6B00]"
          loading={loading}
        />
        <StatCard
          title="This Week"
          value={stats.recentActivity}
          icon={Clock}
          color="text-industrial-text-muted"
          subtitle="New entries"
          loading={loading}
        />
      </div>

      {/* Weighment Status Cards */}
      <div className="space-y-4">
        <h2 className="font-mono text-base font-bold uppercase tracking-wider text-industrial-text flex items-center gap-2">
          <Scale className="h-5 w-5 text-accent" />
          Weighment Status
        </h2>
        <div className="grid gap-4 md:grid-cols-3">
          <div className={`bg-industrial-s1 border p-4 rounded-none text-left ${stats.weighmentStats.pending_weighment_count > 0 ? 'border-[#FFB300] bg-[#FFB300]/5' : 'border-industrial-border'}`}>
            <div className="flex items-center justify-between pb-2 border-b border-industrial-border/30 mb-2">
              <span className="text-xs font-mono font-bold text-industrial-text uppercase flex items-center gap-1.5">
                <AlertTriangle className={`h-4 w-4 ${stats.weighmentStats.pending_weighment_count > 0 ? 'text-[#FFB300]' : 'text-industrial-text-muted'}`} />
                Not Weighed
              </span>
            </div>
            <div>
              <div className={`text-2xl font-mono font-bold ${stats.weighmentStats.pending_weighment_count > 0 ? 'text-[#FFB300]' : 'text-industrial-text-muted'}`}>
                {loading ? '...' : stats.weighmentStats.pending_weighment_count}
              </div>
              <p className="text-[10px] font-mono text-industrial-text-muted mt-1 uppercase tracking-wider">Awaiting factory weighment</p>
            </div>
          </div>
          
          <div className="bg-industrial-s1 border border-industrial-border p-4 rounded-none text-left">
            <div className="flex items-center justify-between pb-2 border-b border-industrial-border/30 mb-2">
              <span className="text-xs font-mono font-bold text-industrial-text uppercase flex items-center gap-1.5">
                <Scale className="h-4 w-4 text-blue-500" />
                Gross Only
              </span>
            </div>
            <div>
              <div className="text-2xl font-mono font-bold text-blue-500">
                {loading ? '...' : stats.weighmentStats.gross_only_count}
              </div>
              <p className="text-[10px] font-mono text-industrial-text-muted mt-1 uppercase tracking-wider">Net weight pending</p>
            </div>
          </div>
          
          <div className="bg-industrial-s1 border border-[#00E676]/20 bg-[#00E676]/5 p-4 rounded-none text-left">
            <div className="flex items-center justify-between pb-2 border-b border-industrial-border/30 mb-2">
              <span className="text-xs font-mono font-bold text-industrial-text uppercase flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-[#00E676]" />
                Net Confirmed
              </span>
            </div>
            <div>
              <div className="text-2xl font-mono font-bold text-[#00E676]">
                {loading ? '...' : stats.weighmentStats.net_confirmed_count}
              </div>
              <p className="text-[10px] font-mono text-industrial-text-muted mt-1 uppercase tracking-wider">Ready for production</p>
            </div>
          </div>
        </div>
      </div>

      {/* Coil Lifecycle Stats */}
      <div className="space-y-4">
        <h2 className="font-mono text-base font-bold uppercase tracking-wider text-industrial-text flex items-center gap-2">
          <Layers className="h-5 w-5 text-accent" />
          Coil Lifecycle
        </h2>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="bg-industrial-s1 border border-industrial-border p-4 rounded-none text-left">
            <div className="flex items-center gap-2 mb-3">
              <span className="led-indicator text-[#00E676] bg-[#00E676]" />
              <span className="font-mono text-xs font-bold text-industrial-text uppercase">OPEN</span>
            </div>
            <div>
              <div className="text-2xl font-mono font-bold text-industrial-text">
                {loading ? '...' : stats.lifecycleStats.OPEN}
              </div>
              <p className="text-[10px] font-mono text-industrial-text-muted mt-1 uppercase tracking-wider">Available for processing</p>
            </div>
          </div>
          
          <div className="bg-industrial-s1 border border-industrial-border p-4 rounded-none text-left">
            <div className="flex items-center gap-2 mb-3">
              <span className="led-indicator text-[#FFB300] bg-[#FFB300]" />
              <span className="font-mono text-xs font-bold text-industrial-text uppercase">ISSUED</span>
            </div>
            <div>
              <div className="text-2xl font-mono font-bold text-industrial-text">
                {loading ? '...' : stats.lifecycleStats.ISSUED}
              </div>
              <p className="text-[10px] font-mono text-industrial-text-muted mt-1 uppercase tracking-wider">Cutting in progress</p>
            </div>
          </div>
          
          <div className="bg-industrial-s1 border border-industrial-border p-4 rounded-none text-left">
            <div className="flex items-center gap-2 mb-3">
              <span className="led-indicator text-[#757575] bg-[#757575]" />
              <span className="font-mono text-xs font-bold text-industrial-text uppercase">CLOSED</span>
            </div>
            <div>
              <div className="text-2xl font-mono font-bold text-industrial-text">
                {loading ? '...' : stats.lifecycleStats.CLOSED}
              </div>
              <p className="text-[10px] font-mono text-industrial-text-muted mt-1 uppercase tracking-wider">Processing complete</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Access */}
      <div className="bg-industrial-s1 border border-industrial-border p-6 rounded-none text-left">
        <h3 className="font-mono text-xs font-bold uppercase tracking-wider text-industrial-text flex items-center gap-2 mb-4 border-b border-industrial-border pb-2">
          <Layers className="h-5 w-5 text-accent" />
          Quick Access Controls
        </h3>
        <div className="grid gap-4 md:grid-cols-2">
          <a
            href="/parties"
            data-testid="quick-access-parties"
            className="p-4 bg-industrial-s2 border border-industrial-border rounded-none hover:border-[#FF6B00] transition-colors text-left flex flex-col justify-between"
          >
            <div>
              <h3 className="font-mono text-sm font-bold uppercase text-industrial-text mb-1">Party Master</h3>
              <p className="text-xs text-industrial-text-muted">Manage client registries and balances</p>
            </div>
          </a>
          <a
            href="/coil-in"
            data-testid="quick-access-coil-in"
            className="p-4 bg-industrial-s2 border border-industrial-border rounded-none hover:border-[#FF6B00] transition-colors text-left flex flex-col justify-between"
          >
            <div>
              <h3 className="font-mono text-sm font-bold uppercase text-industrial-text mb-1">Coil In Entry</h3>
              <p className="text-xs text-industrial-text-muted">Record inward raw coil batches</p>
              {hasPendingWeighments && (
                <span className="mt-2 bg-[#FF1744] text-white font-mono text-[9px] font-bold px-1.5 py-0.5 rounded-sm inline-block uppercase">
                  {stats.weighmentStats.pending_weighment_count} pending
                </span>
              )}
            </div>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
